import { Box, InputGroup, Text } from '@chakra-ui/react';
import { ReactElement, useState, useRef, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { UserIcon, ChevronDownIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { OptionKeyAccessors } from '../types';
import { useDropdownPosition } from '../../../../hooks/multiselect/useDropdownPosition';
import { useClickOutside } from '../../../../hooks/multiselect/useClickOutside';
import { useSearchFilter } from '../../../../hooks/multiselect/useSearchFilter';
import { MultiSelectChips } from './MultiSelectChips';
import { MultiSelectDropdown } from '../MultiSelectDropdown/MultiSelectDropdown';
import { createAccessors } from '../../../../utils/optionAccessors';

const DEFAULT_MAX_VISIBLE_CHIPS = 3;
const FOCUS_DELAY = 100;
const REFOCUS_DELAY = 50;

const DEFAULT_SERVER_SEARCH_HINT =
  'Hinweis: Hier siehst du bis zu 50 Treffer auf einmal. Es fehlt jemand? Nutze einfach die Suche – so findest du Personen über Namen oder E-Mail.';

// Internal normalized option type
type NormalizedOption = {
  value: string;
  label: string;
  secondaryText?: string;
};

export type MultiSelectFilterMode = 'client' | 'server';

type MultiSelectInputProps<T = unknown> = {
  placeholder?: string;
  /** Array of options - can be any data structure */
  options: T[];
  /** Selected values (IDs) */
  selectedValues: string[];
  /** Callback when selection changes - always receives array of IDs */
  onChange: (selectedValues: string[]) => void;
  onRemove?: (value: string) => void;
  width?: string | number;
  minWidth?: string | number;
  maxVisibleChips?: number; // Show count when exceeded
  searchable?: boolean; // Enable search functionality
  searchPlaceholder?: string; // Placeholder for search input
  /** When 'client', options are filtered locally. When 'server', options are assumed already filtered by parent (e.g. via onSearchChange). */
  filterMode?: MultiSelectFilterMode;
  /** Called when search input changes; use with filterMode="server" to drive API search. */
  onSearchChange?: (query: string) => void;
  emptyMessage?: ReactNode; // Message to display when no options are available
  /** Override hint under search; omit to use default when filterMode is server */
  searchHint?: ReactNode;
  icon?: JSX.Element;
} & Omit<OptionKeyAccessors, 'searchKey'>;

/**
 * MultiSelectInput component - A multi-select input with chips and dropdown list
 * Based on Figma design: Input field with selected chips and dropdown list with checkboxes
 *
 * @example
 * // Using key strings
 * <MultiSelectInput
 *   options={users} // [{ id: "1", name: "John", email: "john@example.com" }]
 *   identifierKey="id"
 *   labelKey="name"
 *   secondaryKey="email"
 *   selectedValues={selectedIds}
 *   onChange={setSelectedIds}
 * />
 */
export const MultiSelectInput = <T = unknown,>({
  placeholder = 'Select Names',
  options = [],
  selectedValues = [],
  onChange,
  onRemove,
  width = '100%',
  minWidth = '100%',
  maxVisibleChips = DEFAULT_MAX_VISIBLE_CHIPS,
  searchable = false,
  searchPlaceholder = 'Suchen...',
  filterMode = 'client',
  onSearchChange,
  emptyMessage,
  searchHint,
  icon,
  identifierKey = 'id',
  labelKey = 'label',
  secondaryKey,
}: MultiSelectInputProps<T>): ReactElement => {
  let resolvedSearchHint: ReactNode | undefined;
  if (searchable) {
    if (searchHint !== undefined) {
      resolvedSearchHint = searchHint;
    } else if (filterMode === 'server') {
      resolvedSearchHint = DEFAULT_SERVER_SEARCH_HINT;
    }
  }

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  /** Server search: remember label/secondary per id so chips stay correct when that row is not in the current result set */
  const selectedOptionSnapshotByValueRef = useRef<Map<string, NormalizedOption>>(new Map());

  // Create accessors from key strings
  const accessors = useMemo(
    () =>
      createAccessors({
        identifierKey,
        labelKey,
        secondaryKey,
      }),
    [identifierKey, labelKey, secondaryKey]
  );

  // Normalize options to internal format
  const normalizedOptions = useMemo<NormalizedOption[]>(() => {
    return options.map((option) => ({
      value: accessors.getOptionValue(option),
      label: accessors.getOptionLabel(option),
      secondaryText: accessors.getOptionSecondaryText?.(option),
    }));
  }, [options, accessors]);

  useEffect(() => {
    normalizedOptions.forEach((opt) => {
      selectedOptionSnapshotByValueRef.current.set(opt.value, opt);
    });
  }, [normalizedOptions]);

  // Custom hooks for dropdown positioning and click outside detection
  const { dropdownStyle, maxHeight, isReady } = useDropdownPosition(
    isOpen,
    containerRef,
    dropdownRef
  );

  const handleClose = useCallback((): void => {
    setIsOpen(false);
    if (searchable) {
      setSearchQuery('');
    }
  }, [searchable]);

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  useClickOutside(isOpen, containerRef, dropdownRef, handleClose);

  // Client filter: only when filterMode is 'client' and searchable
  const clientFilteredOptions = useSearchFilter(
    normalizedOptions,
    searchQuery,
    searchable && filterMode === 'client'
  ) as NormalizedOption[];

  // Server mode: options are already filtered by parent; show as-is
  const dropdownOptions = filterMode === 'server' ? normalizedOptions : clientFilteredOptions;

  const resolveOptionForChip = useCallback(
    (value: string): NormalizedOption | undefined =>
      normalizedOptions.find((o) => o.value === value) ??
      selectedOptionSnapshotByValueRef.current.get(value),
    [normalizedOptions]
  );

  const handleToggle = (value: string): void => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
      onRemove?.(value);
    } else {
      const picked = resolveOptionForChip(value);
      if (picked) {
        selectedOptionSnapshotByValueRef.current.set(picked.value, picked);
      }
      onChange([...selectedValues, value]);
      // Refocus search input after a brief delay to allow state update
      if (searchable) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, REFOCUS_DELAY);
      }
    }
  };

  const handleRemoveChip = (value: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    const newSelectedValues = selectedValues.filter((v) => v !== value);
    onChange(newSelectedValues);
    onRemove?.(value);
  };

  const selectedOptions = useMemo(
    () =>
      selectedValues
        .map((value) => resolveOptionForChip(value))
        .filter((o): o is NormalizedOption => o !== undefined),
    [selectedValues, resolveOptionForChip]
  );
  const hasSelectedItems = selectedValues.length > 0;

  // Focus search input when dropdown opens (if searchable)
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      // Small delay to ensure dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, FOCUS_DELAY);
    }
  }, [isOpen, searchable]);

  return (
    <Box ref={containerRef} position="relative" width={width} minWidth={minWidth}>
      {/* Input Field with Chips */}
      <InputGroup
        width="100%"
        startElement={icon || <NovaIcon icon={UserIcon} size="m" color="text.quaternary" />}
        endElement={
          // Custom icon due to missing size
          <ChevronDownIcon
            boxSize="14px"
            color="text.quaternary"
            transform={isOpen ? 'rotate(180deg)' : undefined}
            transition="transform 0.2s"
          />
        }
      >
        <Box
          bg="background.secondary"
          border="default"
          borderColor={isOpen ? 'border.accent.active' : 'border.accent'}
          borderRadius="l"
          h="48px"
          style={{
            padding: '10px 40px',
          }}
          display="flex"
          alignItems="center"
          cursor="pointer"
          onClick={() => setIsOpen(!isOpen)}
          minH="48px"
          minWidth="100%"
          position="relative"
          overflow="hidden"
        >
          {hasSelectedItems ? (
            <MultiSelectChips
              selectedOptions={selectedOptions}
              maxVisibleChips={maxVisibleChips}
              onRemove={handleRemoveChip}
            />
          ) : (
            <Text
              textStyle="body.m.regular"
              color="text.quaternary"
              lineHeight="1.3"
              pl="0"
              flex="1"
            >
              {placeholder}
            </Text>
          )}
        </Box>
      </InputGroup>

      {/* Portal into this container so modal `trapFocus` includes the menu (body portal loses focus). */}
      <MultiSelectDropdown
        isOpen={isOpen}
        dropdownStyle={dropdownStyle}
        maxHeight={maxHeight}
        isReady={isReady}
        portalContainerRef={containerRef}
        dropdownRef={dropdownRef}
        searchable={searchable}
        searchQuery={searchQuery}
        searchPlaceholder={searchPlaceholder}
        searchInputRef={searchInputRef}
        onSearchChange={handleSearchChange}
        onClose={handleClose}
        options={dropdownOptions}
        selectedValues={selectedValues}
        onToggle={handleToggle}
        emptyMessage={emptyMessage}
        searchHint={resolvedSearchHint}
      />
    </Box>
  );
};
